import React, { useState, ChangeEvent, useEffect } from "react";
import {
  X,
  Check,
  ChevronDown,
  ChevronUp,
  Edit,
  Eye,
  EyeOff,
} from "lucide-react";
import { TailSpin } from "react-loader-spinner";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, SubmitHandler } from "react-hook-form";
import { useProjectContext } from "../context/ProjectContext";
import { useNavigate } from "react-router-dom";
import { useCompany } from "../context/CompanyContext";
import appClient from "../utils/appClient";
import axios from "axios";
import { Project } from "../reducer/ProjectReducer";

const systemSchema = z.object({
  system_name: z.string().min(4, "System name is required"),
  owner_first_name: z.string().optional(),
  owner_last_name: z.string().optional(),
  email: z.string().email("Invalid email address").min(3, "min 3 characters"),
  system_type: z.enum(["IT", "OT", "Product"]).optional(),
  shelf_life: z.number().optional(),
  business_criticality: z
    .enum(["Very Low", "Low", "Moderate", "High", "Very High"])
    .optional(),
  upgradability: z
    .enum(["Very Easy", "Easy", "Moderate", "Difficult", "Very Difficult"])
    .optional(),
  crypto_agility: z
    .enum([
      "L0: Initial",
      "L1: Developing",
      "L2: Defined",
      "L3: Managed",
      "L4: Optimized",
    ])
    .optional(),
  // associated_amdb_table: z.string().optional(),
  // associated_cmdb_table: z.string().optional(),
  // associated_data_assets_table: z.string().optional(),
  location: z.string().optional(),
  time_for_migration: z.number(),
  risk_tolerance: z
    .enum(["Very Low", "Low", "Moderate", "High", "Very High"])
    .optional(),
  regulatory_compliance: z.string().optional(),
  // supplied_by: z.string().optional(),
  vendor_supplied_asset: z.enum(["Yes", "No"]).optional(),
  // bu_id : z.string(),
});

type SystemFormData = z.infer<typeof systemSchema>;

type CompanyInfo = {
  company_id: string;
  company_name: string;
  industry_type: string;
  size_of_company?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  country: string;
  pincode?: string;
  primary_contact_first_name?: string;
  primary_contact_last_name?: string;
  primary_contact_email: string;
  primary_contact_phone?: string;
};
type SystemInfo = {
  system_name: string;
  system_id: string;
  owner_first_name?: string;
  owner_last_name?: string;
  email: string;
  system_type?: "IT" | "OT" | "Product";
  shelf_life: number;
  business_criticality?: "Very High" | "High" | "Medium" | "Low" | "Very Low";
  upgradability?:
    | "Very Easy"
    | "Easy"
    | "Moderate"
    | "Difficult"
    | "Very Difficult";
  crypto_agility?:
    | "L0: Initial"
    | "L1: Developing"
    | "L2: Defined"
    | "L3: Managed"
    | "L4: Optimized";
  // associated_amdb_table?: string;
  // associated_cmdb_table?: string;
  // associated_data_assets_table?: string;
  location?: string;
  time_for_migration: number;
  risk_tolerance?: "Very Low" | "Low" | "Moderate" | "High" | "Very High";
  regulatory_compliance?: string;
  // supplied_by?: string;
  vendor_supplied_asset?: "Yes" | "No";
  // bu_id : string;
};

interface BusinessUnit {
  bu_id: string;
  bu_name: string;
  bu_description?: string;
  bu_head_first_name?: string;
  bu_head_last_name?: string;
  bu_head_email: string;
  company_id: string;
  location?: string;
}
const businessUnitSchema = z.object({
  bu_id: z.string().optional(),
  bu_name: z.string().min(1, "Business Unit Name is required"),
  bu_description: z.string().optional(),
  bu_head_first_name: z.string().optional(),
  bu_head_last_name: z.string().optional(),
  bu_head_email: z
    .string()
    .email("Invalid email address")
    .min(3, "Min 3 characters"),
  company_id: z.string().optional(),
  location: z.string().optional(),
});

const companyInfoSchema = z.object({
  company_name: z.string().min(1, "Company Name is required"),
  industry_type: z.string().min(1, "Industry Type is required"),
  address_line_1: z.string().optional(),
  address_line_2: z.string().optional(),
  pincode: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  country: z.string().min(1, "Country is required"),
  primary_contact_first_name: z.string().optional(),
  primary_contact_last_name: z.string().optional(),
  primary_contact_email: z
    .string()
    .email("Invalid email format")
    .min(4, "Email is required"),
  primary_contact_phone: z.string().optional(),
  size_of_company: z.string().optional(),
});
type CompanyInfoFormData = z.infer<typeof companyInfoSchema>;
type BusinessInfoFormData = z.infer<typeof businessUnitSchema>;
// Types
interface ProjectFormData {
  projectId: string;
  projectName: string;
  projectLevel: "Company" | "Business Unit" | "System";
  projectStatus: "Active" | "Hold" | "Completed" | "Yet to Start";
  companyName?: string;
  businessUnitName?: string;
  systemName?: string;
}
type SectionType = "company" | "businessUnit" | "system" | "";

interface SelectOption {
  value: string;
  label: string;
}
interface Company {
  company_id: string;
  company_name: string;
  industry_type: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  country: string;
  primary_contact_first_name: string;
  primary_contact_last_name: string;
  primary_contact_email: string;
  primary_contact_phone: string;
  pincode: string;
  size_of_company: number | null;
}
interface SelectOption {
  value: string;
  label: string;
}
interface ProjectData {
  project_id: string;
}
const ProjectCreationForm: React.FC = () => {
  const { state, dispatch } = useProjectContext();
  const [projectList, setProjectList] = useState<ProjectData[]>([]);
  const [formData, setFormData] = useState<ProjectFormData>({
    projectId: "",
    projectName: state.selected_project?.project_name ?? "",
    projectLevel: "" as ProjectFormData["projectLevel"],
    projectStatus: "Yet to Start",
  });
  const [loading, setLoading] = useState({
    creatingProject: false,
  });
  const [companies, setCompanies] = useState<CompanyInfo[]>([]);
  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([]);
  const [systems, setSystems] = useState<SystemInfo[]>([]);
  const [isEditable, setIsEditable] = useState(false);
  const [isBusinessEditable, setIsBusinessEditable] = useState(false);
  const [isSystemEditable, setIsSystemEditable] = useState(false);
  const [companyOptions, setCompanyOptions] = useState<SelectOption[]>([]);
  const [expandedSection, setExpandedSection] = useState<SectionType>("");
  const [showNewCompanyForm, setShowNewCompanyForm] = useState(false);
  const [showNewBusinessForm, setShowNewBusinessForm] = useState(false);
  const [showNewSystemForm, setShowNewSystemForm] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(
    state.selected_project?.company_id
  );
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState(
    state.selected_project?.bu_id
  );
  const [selectedSystem, setSelectedSystem] = useState(
    state.selected_project?.system_id
  );
  // const [refresh, setRefresh] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<Partial<CompanyInfo> | null>(
    null
  );
  const [businessUnitInfo, setBusinessUnitInfo] =
    useState<Partial<BusinessUnit> | null>(null);
  const [systemsInfo, setSystemsInfo] = useState<Partial<SystemInfo> | null>(
    null
  );
  const navigate = useNavigate();
  const { state: comapanyState, dispatch: companyDispatch } = useCompany();

  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [isSaveButtonEnabled, setSaveIsButtonEnabled] = useState(false);

  // Toggle Address Visibility
  const [showAddress, setShowAddress] = useState(false);
  console.log(state.selected_project);
  const canICreateProject =
    state.selected_project?.project_name?.trim() !== "" &&
    state.selected_project?.project_level !== undefined;

  useEffect(() => {
    setSelectedCompany(state.selected_project?.company_id);
    setSelectedBusinessUnit(state.selected_project?.bu_id);
    setSelectedSystem(state.selected_project?.system_id);
  }, [state.selected_project]);

  console.log(state.selected_project);
  // Update the button's enabled state whenever relevant dependencies change
  useEffect(() => {
    const areAllIdsPresent =
      !!state.company_id && !!state.bu_id && !!state.system_id;
    // const isProjectNameValid = formData.projectName.trim().length > 0;
    const isProjectNameValid =
      state.selected_project?.project_name?.trim() !== "";
    setIsButtonEnabled(areAllIdsPresent && isProjectNameValid);
  }, [state.company_id, state.bu_id, state.system_id, formData.projectName]);

  const fetchCompanies = async () => {
    try {
      // setLoading(true);
      const response = await appClient.get("/api/companies");
      const fetchedCompanies = response.data;
      // console.log(response.data);
      // console.log(companies);
      const allCompanies = [...fetchedCompanies];
      setCompanies(allCompanies);
      const options: SelectOption[] = allCompanies.map((company: Company) => ({
        value: company.company_name
          ? company.company_name.toLowerCase().replace(/\s+/g, "") // format value if company_name exists
          : `company${company.company_id}`, // fallback value if company_name is missing
        label: company.company_name || "Unnamed Company", // fallback label if company_name is missing
      }));
      if (state.selected_project?.company_id) {
        const selectedCompany = allCompanies.find(
          (company) => company.company_id === state.selected_project?.company_id
        );
        // console.log("uodated "+selectedCompany)
        if (selectedCompany) {
          setCompanyInfo(selectedCompany); // Set the company info
          setSelectedCompany(selectedCompany.company_id);
          // console.log("uodated "+companyInfo?.company_id);
        } else {
          setCompanyInfo(null); // If no company matches, reset the company info
        }
      }
      // Add "Company Not Listed" option
      // options.push({ value: "notListed", label: "Company Not Listed" });

      setCompanyOptions(options);
    } catch (err) {
      // setError("Failed to fetch company data.");
      console.error("Error fetching companies:", err);
    } finally {
      // setLoading(false);
    }
  };
  const fetchSingleCompany = () => {};
  useEffect(() => {
    // console.log("changed");
    fetchCompanies();
  }, [state.currentProjectID]);
  const fetchBusinessUnits = async () => {
    try {
      // setLoading(true);
      const response = await appClient.get("/api/business-units");
      const fetchedBusinessUnits = response.data;

      // console.log("Business Unit", response.data);

      // Adding "Business Unit Not Listed" as a fallback option
      const allBusinessUnits = [...fetchedBusinessUnits];

      setBusinessUnits(allBusinessUnits);

      // Map fetched business units to SelectOption format
      const options: SelectOption[] = allBusinessUnits.map(
        (unit: BusinessUnit) => ({
          value: unit.bu_name
            ? unit.bu_name.toLowerCase().replace(/\s+/g, "") // format value if bu_name exists
            : `businessUnit${unit.bu_id}`, // fallback value if bu_name is missing
          label: unit.bu_name || "Unnamed Business Unit", // fallback label if bu_name is missing
        })
      );
      if (state.selected_project?.bu_id) {
        const selectedBu = allBusinessUnits.find(
          (bu) => bu.bu_id === state.selected_project?.bu_id
        );
        if (selectedBu) {
          setBusinessUnitInfo(selectedBu); // Set the company info
          setSelectedBusinessUnit(selectedBu.bu_id);
        } else {
          setBusinessUnitInfo(null); // If no company matches, reset the company info
        }
      }
      // setBusinessUnitOptions(options);
    } catch (err) {
      console.error("Error fetching business units:", err);
      // setError("Failed to fetch business unit data.");
    } finally {
      // setLoading(false);
    }
  };
  useEffect(() => {
    fetchBusinessUnits();
  }, [state.currentProjectID]);

  const fetchSystems = async () => {
    try {
      // setLoading(true); // Uncomment if you want to indicate loading state
      const response = await appClient.get("/api/systems");
      const fetchedSystems = response.data;

      // console.log("Systems", response.data);

      // Adding "System Not Listed" as a fallback option
      const allSystems = [...fetchedSystems];

      setSystems(allSystems);

      // Map fetched systems to SelectOption format
      const options: SelectOption[] = allSystems.map((system: SystemInfo) => ({
        value: system.system_name
          ? system.system_name.toLowerCase().replace(/\s+/g, "") // format value if system_name exists
          : `system${system.system_id}`, // fallback value if system_name is missing
        label: system.system_name || "Unnamed System", // fallback label if system_name is missing
      }));
      if (state.selected_project?.system_id) {
        const selectedSys = allSystems.find(
          (sys) => sys.system_id === state.selected_project?.system_id
        );
        if (selectedSys) {
          setSystemsInfo(selectedSys); // Set the company info
          setSelectedSystem(selectedSys.system_id);
        } else {
          setSystemsInfo(null); // If no company matches, reset the company info
        }
      }
      // setSystemOptions(options); // Uncomment to set options for a dropdown
    } catch (err) {
      console.error("Error fetching systems:", err);
      // setError("Failed to fetch system data.");
    } finally {
      // setLoading(false); // Uncomment if you want to stop loading state
    }
  };
  useEffect(() => {
    fetchSystems();
  }, [state.currentProjectID]);
  const {
    register,
    handleSubmit: onCompanyFormSubmit,
    reset,
    formState: { errors },
  } = useForm<CompanyInfoFormData>({
    resolver: zodResolver(companyInfoSchema),
  });

  const {
    register: registerBusinessUnit,
    handleSubmit: onBusinessUnitFormSubmit,
    reset: resetBusinessUnit,
    formState: { errors: businessUnitErrors },
  } = useForm<BusinessInfoFormData>({
    resolver: zodResolver(businessUnitSchema),
  });
  const {
    register: registerSystem,
    handleSubmit: onSystemFormSubmit,
    reset: resetSystem,
    formState: { errors: systemErrors },
  } = useForm<SystemFormData>({
    resolver: zodResolver(systemSchema),
  });

  const onSubmit = (data: BusinessInfoFormData) => {
    // console.log("Form Data:", data);
  };
  const handleCompanyChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    const selectedValue = event.target.value;
    setSelectedCompany(selectedValue);
    // console.log("Selected Value",selectedValue)
    // console.log(selectedCompany);
    // dispatch({ type: "SET_COMPANY_ID", payload: selectedValue });
    if (selectedValue === "not-listed") {
      setShowNewCompanyForm(true);
      setCompanyInfo(null); // Clear company info if starting a new company entry
    } else {
      setShowNewCompanyForm(false);
      dispatch({ type: "SET_COMPANY_ID", payload: selectedValue });
      const selectedCompanyInfo = companies.find(
        (c) => c.company_id === selectedValue
      );
      // console.log("Selected CompanyChange ID from context:", state.company_id);
      if (selectedCompanyInfo) {
        // Dispatch action to update global state
        dispatch({
          type: "SET_SELECTED_PROJECT",
          payload: {
            ...state.selected_project, // Keep the existing selected project data
            company_id: selectedCompanyInfo.company_id, // Update the specific field
          } as Project,
        });
        companyDispatch({
          type: "SET_SELECTED_COMPANY_NAME",
          payload: selectedCompanyInfo.company_name,
        });
        setCompanyInfo(selectedCompanyInfo);
      }
    }
  };

  const handleEditButtonClick = () => {
    setIsEditable(true);
  };
  const handleBusinessEditButtonClick = () => {
    setIsBusinessEditable(true);
  };
  const handleSystemEditButtonClick = () => {
    setIsSystemEditable(true);
  };

  const handleBusinessUnitChange = (
    event: ChangeEvent<HTMLSelectElement>
  ): void => {
    setSelectedBusinessUnit(event.target.value);
    const selectedValue = event.target.value;
    // console.log(selectedValue);
    if (selectedValue === "not-listed") {
      setShowNewBusinessForm(true);
      setBusinessUnitInfo(null);
    } else {
      setShowNewBusinessForm(false);
      dispatch({ type: "SET_BU_ID", payload: selectedValue });
      const selectedBusinessUnitInfo = businessUnits.find(
        (b) => b.bu_id === selectedValue
      );
      if (selectedBusinessUnitInfo) {
        setBusinessUnitInfo(selectedBusinessUnitInfo);
        dispatch({
          type: "SET_SELECTED_PROJECT",
          payload: {
            ...state.selected_project, // Keep the existing selected project data
            bu_id: selectedBusinessUnitInfo.bu_id, // Update the specific field
          } as Project,
        });
      }
    }
  };

  const handleSystemChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    setSelectedSystem(event.target.value);
    const selectedValue = event.target.value;
    // console.log(selectedValue);
    if (selectedValue === "not-listed") {
      setShowNewSystemForm(true);
      // setBusinessUnitInfo(null);
      setSystemsInfo(null);
    } else {
      setShowNewSystemForm(false);
      dispatch({ type: "SET_SYSTEM_ID", payload: selectedValue });
      const selectedSystemInfo = systems.find(
        (s) => s.system_id === selectedValue
      );
      if (selectedSystemInfo) {
        setSystemsInfo(selectedSystemInfo);
        dispatch({
          type: "SET_SELECTED_PROJECT",
          payload: {
            ...state.selected_project, // Keep the existing selected project data
            system_id: selectedSystemInfo.system_id, // Update the specific field
          } as Project,
        });
        dispatch({
          type: "SET_CURRENT_PROJECT_SYSTEM_INFO",
          payload: selectedSystemInfo,
        });
      }
    }
  };
  const toggleSection = (section: SectionType): void => {
    setExpandedSection(expandedSection === section ? "" : section);
  };

  const handleProjectLevelChange = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    const newProjectLevel = event.target
      .value as ProjectFormData["projectLevel"];
    setFormData({
      ...formData,
      projectLevel: event.target.value as ProjectFormData["projectLevel"],
    });
    // Dispatch action to set the selected project with the updated project level
    dispatch({
      type: "SET_SELECTED_PROJECT",
      payload: {
        ...state.selected_project, // Keep the existing selected project data
        project_level: newProjectLevel, // Update only the project level
      } as Project, // Cast to Project type
    });
  };

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ): void => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    dispatch({
      type: "SET_SELECTED_PROJECT",
      payload: {
        ...state.selected_project, // Keep the existing selected project data
        [name]: value, // Update the specific field that changed
      } as Project, // Cast to Project type to ensure the payload matches the expected type
    });
    // console.log(state.selected_project?.project_name)
  };

  // useEffect(() => {
  //   console.log("late: "+state.selected_project?.project_name);
  // }, [handleInputChange]);
  const newProject = {
    project_name: state.selected_project?.project_name,
    created_by_user_id: localStorage.getItem("userId"), // Replace with the logged-in user's ID
    project_level: state.selected_project?.project_level,
    project_status: "Active",
    company_id: "CPY001",
    bu_id: state.bu_id,
    system_id: state.system_id,
    project_end_date: null, // Optional, can set later if required
  };
  const handleCreateProject = async () => {
    setLoading({ ...loading, creatingProject: true });
    try {
      console.log(newProject);
      const response = await appClient.post("/api/projects", newProject);
      if (response.status === 201) {
        // console.log("Project created successfully:", response.data);
        dispatch({ type: "SET_PROJECT_NAME", payload: formData.projectName });
        alert("Project created successfully!");
        localStorage.setItem("lastAssessment", response.data.data.project_id);
        // console.log(response.data.data.project_id);
        navigate("/device");
      }
    } catch (error) {
      console.error("Error creating project:", error);
      if (axios.isAxiosError(error) && error.response) {
        console.error("Error response data:", error.response.data);
        alert(
          `Error: ${error.response.data.error || "Failed to create project"}`
        );
      } else {
        alert("An unexpected error occurred");
      }
    } finally {
      setLoading({ ...loading, creatingProject: false });
    }
  };
  const updateProject = {
    project_name: state.selected_project?.project_name,
    created_by_user_id: localStorage.getItem("userId"), // Replace with the logged-in user's ID
    project_level: state.selected_project?.project_level,
    project_status: "Active",
    company_id: state.selected_project?.company_id,
    bu_id: state.selected_project?.bu_id,
    system_id: state.selected_project?.system_id,
    project_end_date: null, // Optional, can set later if required
    assessment_scope: JSON.stringify(state.selected_project?.assessment_scope),
  };
  const handleSaveProject = async () => {
    try {
      // Use the project_id from selected_project in the URL
      const projectId = state.selected_project?.project_id;
      // console.log(projectId);
      if (!projectId) {
        alert("Project ID is missing");
        return;
      }
      // console.log(state.selected_project);
      // console.log(updateProject);

      const response = await appClient.put(
        `/api/projects/${projectId}`,
        updateProject
      );

      if (response.status === 200) {
        // 200 for successful update (not 201)
        // console.log("Project updated successfully:", response.data);
        dispatch({ type: "SET_PROJECT_NAME", payload: formData.projectName });
        alert("Project updated successfully!");
        navigate("/device");
      }
    } catch (error) {
      console.error("Error updating project:", error);
      if (axios.isAxiosError(error) && error.response) {
        console.error("Error response data:", error.response.data);
        alert(
          `Error: ${error.response.data.error || "Failed to update project"}`
        );
      } else {
        alert("An unexpected error occurred");
      }
    }
  };
  const handleReset = (): void => {
    setFormData({
      projectId: "DIS20240001",
      projectName: "",
      projectLevel: "System",
      projectStatus: "Yet to Start",
    });
    setExpandedSection("");
  };
  const handleCompanyDetailsSubmit: SubmitHandler<CompanyInfoFormData> = async (
    data
  ) => {
    // console.log("Form submitted successfully:", data);
    try {
      const response = await appClient.post("/api/companies", data);
      // console.log(response.data);
      const companyId = response.data.data.company_id;
      if (response.status === 201) {
        reset();
        const newlyAddedCompany = response.data.data;
        setCompanyInfo(newlyAddedCompany);
        dispatch({ type: "SET_COMPANY_ID", payload: companyId });
        companyDispatch({
          type: "SET_SELECTED_COMPANY_NAME",
          payload: newlyAddedCompany.company_name,
        });
        dispatch({
          type: "SET_SELECTED_PROJECT",
          payload: {
            ...state.selected_project, // Keep the existing selected project data
            company_id: newlyAddedCompany.company_id, // Update the specific field
          } as Project, // Cast the object to `Project` type
        }); // setSelectedBusinessUnit("");
        fetchCompanies();
        // console.log("Selected Company ID from context:", state.company_id);
        alert("data submitted successfull");
        setSelectedCompany(companyId);
      }
      if (response.status === 400) {
        alert("data entry failde due to required");
      }
      setShowNewCompanyForm(false);
      // setExpandedSection("");
    } catch (error) {
      console.log(error);
    }
  };
  const handleBusinessDetailsSubmit: SubmitHandler<
    BusinessInfoFormData
  > = async (data) => {
    // console.log("Form submitted successfully:", data);
    // console.log(selectedCompany);
    try {
      const response = await appClient.post("/api/business-units", {
        ...data,
        company_id: state.company_id, // Add selectedCompany as company_id
      });
      // console.log(response.data);
      const businessUnitId = response.data.data.bu_id;
      if (response.status === 201) {
        resetBusinessUnit();
        const newlyAddedBU = response.data.data;
        setBusinessUnitInfo(newlyAddedBU);
        dispatch({ type: "SET_BU_ID", payload: businessUnitId });
        dispatch({
          type: "SET_SELECTED_PROJECT",
          payload: {
            ...state.selected_project, // Keep the existing selected project data
            bu_id: businessUnitId, // Update the specific field
          } as Project, // Cast the object to `Project` type
        }); // setSelectedBusinessUnit("");
        fetchBusinessUnits();
        alert("Business Unit added successfull");
        setSelectedBusinessUnit(businessUnitId);
      }
      if (response.status === 400) {
        alert("data entry failde due to required");
      }
      setShowNewBusinessForm(false);
    } catch (error) {
      console.log(error);
    }
  };
  const handleSystemDetailsSubmit: SubmitHandler<SystemFormData> = async (
    data
  ) => {
    // console.log("System form submitted successfully:", data);
    // console.log(selectedBusinessUnit);
    // console.log("IN SYstem SUbmit ");
    // console.log(data);
    try {
      const response = await appClient.post("/api/systems", {
        ...data,
        bu_id: state.bu_id,
      });
      const systemId = response.data.data.system_id;
      if (response.status === 201) {
        resetSystem();
        // setSelectedSystem("");
        const newlyAddedSystem = response.data.data;
        setSystemsInfo(newlyAddedSystem);
        dispatch({ type: "SET_SYSTEM_ID", payload: systemId });
        dispatch({
          type: "SET_SELECTED_PROJECT",
          payload: {
            ...state.selected_project, // Keep the existing selected project data
            system_id: systemId, // Update the specific field
          } as Project, // Cast the object to `Project` type
        }); // setSelectedBusinessUnit("");
        dispatch({
          type: "SET_CURRENT_PROJECT_SYSTEM_INFO",
          payload: newlyAddedSystem,
        });
        fetchSystems();
        alert("System added successfully");
        setSelectedSystem(systemId);
      } else if (response.status === 400) {
        alert("Data entry failed due to missing required fields");
      }
      setShowNewSystemForm(false);
    } catch (error) {
      console.error("Error submitting system details:", error);
    }
  };

  const handleEditCompany = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedCompany || selectedCompany === "not-listed" || !companyInfo) {
      return;
    }
    const { company_id, ...updateData } = companyInfo;
    try {
      const response = await appClient.put(
        `/api/companies/${selectedCompany}`,
        updateData
      );

      if (response.status === 200) {
        // console.log("Company updated successfully");
        const { message, data } = response.data;
        alert(message);
        // Refresh the companies list after a successful update
        fetchCompanies();
        setIsEditable(false);
      }
    } catch (error) {
      console.error("Error updating company:", error);
    }
  };
  const handleEditBusinessUnit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    if (
      !selectedBusinessUnit ||
      selectedBusinessUnit === "not-listed" ||
      !businessUnitInfo
    ) {
      return;
    }
    const { bu_id, ...updateData } = businessUnitInfo;
    try {
      const response = await appClient.put(
        `/api/business-units/${selectedBusinessUnit}`,
        {
          ...updateData,
          company_id: selectedCompany,
        }
      );

      if (response.status === 200) {
        // console.log("Business unit updated successfully");
        const { message, data } = response.data;
        alert(message);
        // Refresh the business units list after a successful update
        fetchBusinessUnits();
        // setSelectedBusinessUnit("");
        setIsBusinessEditable(false);
      }
    } catch (error) {
      console.error("Error updating business unit:", error);
    }
  };
  const handleEditSystem = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Ensure that the system is selected and system information is available
    if (!selectedSystem || selectedSystem === "not-listed" || !systemsInfo) {
      return;
    }

    try {
      // Create a shallow copy of systemsInfo and exclude system_id
      const { system_id, ...updateData } = systemsInfo;

      const response = await appClient.put(
        `/api/systems/${selectedSystem}`, // use selectedSystem in the URL
        {
          ...updateData, // include all system info fields to be updated
        }
      );

      if (response.status === 200) {
        // console.log("System updated successfully");
        const { message } = response.data;
        alert(message);
        // Refresh the systems list after a successful update
        fetchSystems();
        setIsSystemEditable(false);
      }
    } catch (error) {
      console.error("Error updating system:", error);
    }
  };

  const handleEditReset = () => {
    if (selectedCompany && selectedCompany !== "not-listed") {
      const originalCompanyData = companies.find(
        (company) => company.company_id.toString() === selectedCompany
      );
      if (originalCompanyData) {
        setCompanyInfo(originalCompanyData);
      }
    }
  };
  const handleBusinessEditReset = () => {
    if (selectedBusinessUnit && selectedBusinessUnit !== "not-listed") {
      const originalBUData = businessUnits.find(
        (unit) => unit.bu_id.toString() === selectedBusinessUnit
      );
      if (originalBUData) {
        setBusinessUnitInfo(originalBUData);
      }
    }
  };
  const handleCompanyInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCompanyInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleBUInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setBusinessUnitInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleSystemInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    setSystemsInfo((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };
  // Fetch project list from API
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await appClient.get(
          `/api/projects/user/${localStorage.getItem("userId")}`
        ); // Replace with your API endpoint
        console.log(response);
        setProjectList(response.data); // Set the fetched project list
        // dispatch({
        //   type: "SET_PROJECTS",
        //   payload: response.data, // Pass the Project or null if you want to reset
        // });
        // console.log(response.data);
      } catch (error) {
        console.error("Error fetching project data:", error);
      }
    };

    fetchProjects();
  }, []); //refresh

  return (
    // <div className="p-4 overflow-y-auto min-h-[82vh] md:h-[80vh] lg:h-[70vh] xl:h-[65vh] custom-scrollbar">
    <div className="p-2 bg-white rounded-lg h-[calc(100vh-116px)] overflow-y-auto custom-scrollbar">
      <div className=" justify-between items-center bg-white rounded-t-lg">
        <div className="flex justify-between items-center mb-2">
          <h1 className="text-xl font-bold text-custom-blue7 pl-4">
            Project Creation
          </h1>
          <button
            className="p-2 bg-custom-blue6 rounded-full"
            onClick={() => {
              navigate("/");
            }}
          >
            <X className="text-gray-50 xl:h-5 xl:w-5 lg:h-4 lg:w-4" />
          </button>
        </div>
        <div className="p-8 bg-custom-blue0 rounded-lg shadow-lg">
          {/* Mandatory Fields Note */}
          <div className="text-red-500 text-sm mb-2">* Mandatory fields</div>
          {/* Project Details */}
          <div className="space-y-4 mb-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="projectId"
                  className="block text-sm font-medium text-custom-blue6 mb-1"
                >
                  Project ID
                </label>
                <input
                  id="projectId"
                  type="text"
                  name="projectId"
                  // value={formData.projectId}
                  disabled
                  className="w-full p-2 border rounded bg-gray-50 text-gray-500"
                  placeholder={
                    state.selected_project?.project_id ??
                    state.currentProjectID ??
                    undefined
                  }
                />
              </div>

              <div>
                <label
                  htmlFor="projectName"
                  className="block text-sm font-medium text-custom-blue6 mb-1"
                >
                  Project Name<span className="text-red-500">*</span>
                </label>
                <input
                  id="project_name"
                  type="text"
                  name="project_name"
                  // value={formData.projectName}
                  value={state.selected_project?.project_name ?? ""}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded outline-none focus:ring-1 focus:ring-custom-blue5 focus:border-custom-blue5"
                  placeholder="Myproject1"
                  required
                />
              </div>
            </div>

            {/* Project Level Radio Buttons */}
            <div>
              <label className="block text-sm font-medium text-custom-blue6">
                Project Level<span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-6">
                {(["Company", "Business Unit", "System"] as const).map(
                  (level) => (
                    <label key={level} className="flex items-center">
                      <input
                        type="radio"
                        name="project_level"
                        value={level}
                        checked={
                          state.selected_project?.project_level === level
                        }
                        onChange={handleProjectLevelChange}
                        disabled={
                          level === "Company" || level === "Business Unit"
                        }
                        className="appearance-none outline-none h-3 w-3 border border-custom-blue7 rounded-full checked:border-custom-blue7 checked:bg-custom-blue7 checked:ring-1 checked:ring-offset-1 checked:ring-custom-blue7 relative"
                      />
                      <span className="ml-4 capitalize text-gray-500">
                        {level.replace(/([A-Z])/g, " $1").trim()}
                      </span>
                    </label>
                  )
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 ">
          {/* Collapsible Sections */}
          {state.selected_project?.project_level === "System" && (
            <>
              {/* Business Unit Information */}
              <div className="border rounded-lg mb-4 bg-custom-blue0 shadow-lg">
                <div
                  className="flex justify-between items-center p-4 cursor-pointer"
                  role="button"
                  tabIndex={0}
                >
                  <h3 className="text-custom-blue6 font-medium">
                    Business Unit Information
                  </h3>
                  <div className="flex items-center">
                    {selectedBusinessUnit &&
                    selectedBusinessUnit !== "notListed" &&
                    businessUnits.length > 0
                      ? (() => {
                          // Find the selected business unit by its ID
                          const selectedBusinessUnitData = businessUnits.find(
                            (unit) => unit.bu_id === selectedBusinessUnit
                          );

                          // If a business unit is found, display its name
                          return selectedBusinessUnitData ? (
                            <p className="p-2 text-custom-blue7 font-semibold mr-2">
                              {selectedBusinessUnitData.bu_name}
                            </p>
                          ) : (
                            <p className="p-2 text-custom-blue7 font-semibold mr-2">
                              Kindly Add Business Unit
                            </p>
                          );
                        })()
                      : null}
                  </div>
                  <div className="flex items-center ">
                    {selectedBusinessUnit &&
                      selectedBusinessUnit !== "not-listed" && (
                        <button
                          onClick={handleBusinessEditButtonClick}
                          className="p-2 bg-custom-blue6 rounded-full mr-2"
                        >
                          <Edit className="text-gray-50 xl:h-5 xl:w-5 lg:h-4 lg:w-4" />
                        </button>
                      )}
                    {/* <button className="p-2 bg-custom-blue6 rounded-full mr-2">
                      <Check size={20} className="text-gray-50" />
                    </button> */}

                    {expandedSection === "businessUnit" ? (
                      <button className="p-2 bg-custom-blue7 rounded-full">
                        <ChevronUp
                          className="text-gray-50 xl:h-5 xl:w-5 lg:h-4 lg:w-4"
                          onClick={() => toggleSection("businessUnit")}
                        />
                      </button>
                    ) : (
                      <button className="p-2 bg-custom-blue7 rounded-full">
                        <ChevronDown
                          className="text-gray-50 xl:h-5 xl:w-5 lg:h-4 lg:w-4"
                          onClick={() => toggleSection("businessUnit")}
                        />
                      </button>
                    )}
                  </div>
                </div>
                {expandedSection === "businessUnit" && (
                  <div className="p-4 border-t">
                    <label
                      htmlFor="businessUnitName"
                      className="block text-sm font-medium text-custom-blue6 mb-1"
                    >
                      Business Unit Name<span className="text-red-500">*</span>
                    </label>
                    <select
                      id="businessUnitName"
                      name="businessUnitName"
                      value={selectedBusinessUnit}
                      onChange={(e) => {
                        const selectedValue = e.target.value;
                        handleBusinessUnitChange(e); // Keep this if it handles other necessary logic
                      }}
                      className="w-full p-2 border rounded"
                      required
                    >
                      <option value="">Select</option>
                      {businessUnits.map((businessUnit) => (
                        <option
                          key={businessUnit.bu_id}
                          value={businessUnit.bu_id}
                        >
                          {businessUnit.bu_name}
                        </option>
                      ))}
                      <option value="not-listed">
                        Business Unit Not Listed
                      </option>
                    </select>
                    {selectedBusinessUnit &&
                      selectedBusinessUnit !== "not-listed" &&
                      (isBusinessEditable ? (
                        <form onSubmit={handleEditBusinessUnit}>
                          <div>
                            <div className="grid grid-cols-1 gap-4 mt-4 w-1/2">
                              <div className="">
                                <label className="block text-custom-blue6 text-sm font-medium mb-2">
                                  Business Unit Name
                                  <span className="text-red-500">*</span>
                                </label>
                                <input
                                  name="bu_name"
                                  value={businessUnitInfo?.bu_name}
                                  // {...registerBusinessUnit("bu_name")}
                                  onChange={handleBUInputChange}
                                  type="text"
                                  className="w-full
                               p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                  required
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-1 gap-4 mt-4">
                              <div className="">
                                <label className="block text-sm font-medium mb-2 text-custom-blue6">
                                  Unit Description
                                </label>
                                <input
                                  name="bu_description"
                                  value={businessUnitInfo?.bu_description}
                                  onChange={handleBUInputChange}
                                  // {...registerBusinessUnit("bu_description")}
                                  type="textarea"
                                  className="w-full p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                  // required
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-1 gap-4 mt-4">
                              <div className="">
                                <label className="block text-sm font-medium mb-2 text-custom-blue6">
                                  Location
                                </label>
                                <input
                                  name="location"
                                  value={businessUnitInfo?.location}
                                  onChange={handleBUInputChange}
                                  // {...registerBusinessUnit("location")}
                                  type="text"
                                  className="w-1/2 p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                />
                                {/* {errors.companyName && (
                          <span className="text-red-500">
                            {errors.companyName.message}
                          </span>
                        )} */}
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4 mt-4">
                              <div className="">
                                <label className="block text-sm font-medium text-custom-blue6 mb-2">
                                  Business Unit Head First Name
                                </label>
                                <input
                                  name="bu_head_first_name"
                                  value={businessUnitInfo?.bu_head_first_name}
                                  onChange={handleBUInputChange}
                                  // {...registerBusinessUnit("bu_head_first_name")}
                                  type="text"
                                  className="w-full p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                />
                                {/* {errors.companyName && (
                          <span className="text-red-500">
                            {errors.companyName.message}
                          </span>
                        )} */}
                              </div>
                              <div className="">
                                <label className="block text-sm font-medium text-custom-blue6 mb-2">
                                  Last Name
                                </label>
                                <input
                                  onChange={handleBUInputChange}
                                  name="bu_head_last_name"
                                  value={businessUnitInfo?.bu_head_last_name}
                                  type="text"
                                  className="w-full p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                />
                                {/* {errors.companyName && (
                          <span className="text-red-500">
                            {errors.companyName.message}
                          </span>
                        )} */}
                              </div>
                              <div className="">
                                <label className="block text-sm font-medium mb-2 text-custom-blue6">
                                  Email<span className="text-red-500">*</span>
                                </label>
                                <input
                                  // name="primary_contact_email"
                                  name="bu_head_email"
                                  value={businessUnitInfo?.bu_head_email}
                                  onChange={handleBUInputChange}
                                  // {...registerBusinessUnit("bu_head_email")}
                                  type="text"
                                  className="w-full p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                  required
                                />
                                {/* {errors.companyName && (
                          <span className="text-red-500">
                            {errors.companyName.message}
                          </span>
                        )} */}
                              </div>
                            </div>
                            <div className="flex justify-center mt-4 items-center space-x-4">
                              <button
                                type="button"
                                onClick={() => handleBusinessEditReset()}
                                className="px-4 py-2 bg-custom-blue5 text-white rounded hover:bg-custom-blue6"
                              >
                                Reset
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setIsBusinessEditable(false);
                                  // setExpandedSection(""); // Close the expanded section
                                  setSelectedBusinessUnit(""); // Deselect the company
                                  setShowNewBusinessForm(false);
                                }}
                                className="px-4 py-2 bg-custom-blue5 text-white rounded hover:bg-custom-blue6"
                              >
                                Cancel
                              </button>
                              <button
                                type="submit"
                                className="px-4 py-2 bg-custom-blue6 text-white rounded hover:bg-custom-blue7"
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        </form>
                      ) : (
                        <div>
                          <div className="grid grid-cols-1 gap-4 mt-4 w-1/2">
                            <div className="">
                              <label className="block text-custom-blue6 text-sm font-medium mb-2">
                                Business Unit Name
                              </label>

                              <p
                                className="w-full
                               p-2 border border-custom-blue3 rounded"
                              >
                                {businessUnitInfo?.bu_name}
                              </p>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 gap-4 mt-4">
                            <div className="">
                              <label className="block text-sm font-medium mb-2 text-custom-blue6">
                                Unit Description
                              </label>

                              <p
                                className={`w-full p-2 border border-custom-blue3 rounded ${
                                  !businessUnitInfo?.bu_description &&
                                  "h-[41px]"
                                }`}
                              >
                                {businessUnitInfo?.bu_description}
                              </p>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 gap-4 mt-4">
                            <div className="">
                              <label className="block text-sm font-medium mb-2 text-custom-blue6">
                                Location
                              </label>

                              <p
                                className={`w-1/2 p-2 border border-custom-blue3 rounded ${
                                  !businessUnitInfo?.location && "h-[41px]"
                                }`}
                              >
                                {businessUnitInfo?.location}
                              </p>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4 mt-4">
                            <div className="">
                              <label className="block text-sm font-medium text-custom-blue6 mb-2">
                                Business Unit Head
                              </label>

                              <p
                                className={`w-full p-2 border border-custom-blue3 rounded ${
                                  (!businessUnitInfo?.bu_head_first_name ||
                                    !businessUnitInfo?.bu_head_last_name) &&
                                  "h-[41px]"
                                }`}
                              >
                                {`${businessUnitInfo?.bu_head_first_name} ${businessUnitInfo?.bu_head_last_name}`}
                              </p>
                            </div>

                            <div className="">
                              <label className="block text-sm font-medium mb-2 text-custom-blue6">
                                Email
                              </label>

                              <p className="w-full p-2 border border-custom-blue3 rounded">
                                {businessUnitInfo?.bu_head_email}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    {showNewBusinessForm && (
                      <form
                        onSubmit={onBusinessUnitFormSubmit(
                          handleBusinessDetailsSubmit
                        )}
                      >
                        <div>
                          <div className="grid grid-cols-1 gap-4 mt-4 w-1/2">
                            <div className="">
                              <label className="block text-custom-blue6 text-sm font-medium mb-2">
                                Business Unit Name
                                <span className="text-red-500">*</span>
                              </label>
                              <input
                                // name="company_name"
                                // value={companyInfo.company_name}
                                {...registerBusinessUnit("bu_name")}
                                // onChange={handleCompanyInputChange}
                                type="text"
                                className="w-full
                               p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                required
                              />
                              {/* {errors.companyName && (
                          <span className="text-red-500">
                            {errors.companyName.message}
                          </span>
                        )} */}
                            </div>
                          </div>
                          <div className="grid grid-cols-1 gap-4 mt-4">
                            <div className="">
                              <label className="block text-sm font-medium mb-2 text-custom-blue6">
                                Unit Description
                                {/* <span className="text-red-500">*</span> */}
                              </label>
                              <input
                                // name="industry_type"
                                // onChange={handleCompanyInputChange}
                                {...registerBusinessUnit("bu_description")}
                                type="textarea"
                                className="w-full p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                              />
                              {/* {errors.companyName && (
                          <span className="text-red-500">
                            {errors.companyName.message}
                          </span>
                        )} */}
                            </div>
                          </div>
                          <div className="grid grid-cols-1 gap-4 mt-4">
                            <div className="">
                              <label className="block text-sm font-medium mb-2 text-custom-blue6">
                                Location
                              </label>
                              <input
                                // name="industry_type"
                                // onChange={handleCompanyInputChange}
                                {...registerBusinessUnit("location")}
                                type="text"
                                className="w-1/2 p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                              />
                              {/* {errors.companyName && (
                          <span className="text-red-500">
                            {errors.companyName.message}
                          </span>
                        )} */}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4 mt-4">
                            <div className="">
                              <label className="block text-sm font-medium text-custom-blue6 mb-2">
                                Business Unit Head First Name
                              </label>
                              <input
                                // name="primary_contact_first_name"
                                // onChange={handleCompanyInputChange}
                                {...registerBusinessUnit("bu_head_first_name")}
                                type="text"
                                className="w-full p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                              />
                              {/* {errors.companyName && (
                          <span className="text-red-500">
                            {errors.companyName.message}
                          </span>
                        )} */}
                            </div>
                            <div className="">
                              <label className="block text-sm font-medium text-custom-blue6 mb-2">
                                Last Name
                              </label>
                              <input
                                // name="primary_contact_last_name"
                                // onChange={handleCompanyInputChange}
                                {...registerBusinessUnit("bu_head_last_name")}
                                type="text"
                                className="w-full p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                              />
                              {/* {errors.companyName && (
                          <span className="text-red-500">
                            {errors.companyName.message}
                          </span>
                        )} */}
                            </div>
                            <div className="">
                              <label className="block text-sm font-medium mb-2 text-custom-blue6">
                                Email<span className="text-red-500">*</span>
                              </label>
                              <input
                                // name="primary_contact_email"
                                // onChange={handleCompanyInputChange}
                                {...registerBusinessUnit("bu_head_email")}
                                type="text"
                                className="w-full p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                required
                              />
                              {/* {errors.companyName && (
                          <span className="text-red-500">
                            {errors.companyName.message}
                          </span>
                        )} */}
                            </div>
                          </div>
                          <div className="flex justify-center mt-4 items-center space-x-4">
                            <button
                              type="button"
                              onClick={() => {
                                resetBusinessUnit(); // Reset form fields
                                // setSelectedCompany(""); // Reset selected company
                                // setShowNewCompanyForm(false); // Optionally hide the form
                              }}
                              className="px-4 py-2 bg-custom-blue5 text-white rounded hover:bg-custom-blue6"
                            >
                              Reset
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setExpandedSection(""); // Close the expanded section
                                setSelectedBusinessUnit(""); // Deselect the company
                                setShowNewBusinessForm(false);
                              }}
                              className="px-4 py-2 bg-custom-blue5 text-white rounded hover:bg-custom-blue6"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="px-4 py-2 bg-custom-blue6 text-white rounded hover:bg-custom-blue7"
                            >
                              Add New Business Unit
                            </button>
                          </div>
                        </div>
                      </form>
                    )}
                  </div>
                )}
              </div>

              {/* System Information */}
              <div className="border rounded-lg mb-6 bg-custom-blue0 shadow-lg">
                <div
                  className="flex justify-between items-center p-4 cursor-pointer"
                  role="button"
                  tabIndex={0}
                >
                  <h3 className="text-custom-blue6 font-medium">
                    System Information
                  </h3>
                  <div className="flex items-center">
                    {selectedSystem &&
                    selectedSystem !== "notListed" &&
                    systems.length > 0
                      ? (() => {
                          // Find the selected system by its ID
                          const selectedSystemData = systems.find(
                            (system) => system.system_id === selectedSystem
                          );

                          // If a system is found, display its name
                          return selectedSystemData ? (
                            <p className="p-2 text-custom-blue7 font-semibold mr-2">
                              {selectedSystemData.system_name}
                            </p>
                          ) : (
                            <p className="p-2 text-custom-blue7 font-semibold mr-2">
                              Kindly Add System
                            </p>
                          );
                        })()
                      : null}
                  </div>

                  <div className="flex items-center ">
                    {selectedSystem && selectedSystem !== "not-listed" && (
                      <button
                        onClick={handleSystemEditButtonClick}
                        className="p-2 bg-custom-blue6 rounded-full mr-2"
                      >
                        <Edit className="text-gray-50 xl:h-5 xl:w-5 lg:h-4 lg:w-4" />
                      </button>
                    )}
                    {/* <button className="p-2 bg-custom-blue6 rounded-full mr-2">
                      <Check size={20} className="text-gray-50" />
                    </button> */}

                    {expandedSection === "system" ? (
                      <button className="p-2 bg-custom-blue7 rounded-full">
                        <ChevronUp
                          className="text-gray-50 xl:h-5 xl:w-5 lg:h-4 lg:w-4"
                          onClick={() => toggleSection("system")}
                        />
                      </button>
                    ) : (
                      <button className="p-2 bg-custom-blue7 rounded-full">
                        <ChevronDown
                          className="text-gray-50 xl:h-5 xl:w-5 lg:h-4 lg:w-4"
                          onClick={() => toggleSection("system")}
                        />
                      </button>
                    )}
                  </div>
                </div>
                {expandedSection === "system" && (
                  <div className="p-4 border-t">
                    <label
                      htmlFor="systemName"
                      className="block text-sm font-medium text-custom-blue6 mb-1"
                    >
                      System Name<span className="text-red-500">*</span>
                    </label>
                    <select
                      id="systemName"
                      name="systemName"
                      value={selectedSystem}
                      onChange={(e) => {
                        const selectedValue = e.target.value;
                        handleSystemChange(e); // Keep this if it handles other necessary logic
                      }}
                      className="w-full p-2 border rounded"
                      required
                    >
                      <option value="">Select</option>
                      {systems.map((system) => (
                        <option key={system.system_id} value={system.system_id}>
                          {system.system_name}
                        </option>
                      ))}
                      <option value="not-listed">System Not Listed</option>
                    </select>
                    {selectedSystem &&
                      selectedSystem !== "not-listed" &&
                      (isSystemEditable ? (
                        <form onSubmit={handleEditSystem}>
                          <div>
                            <div className="grid grid-cols-1 gap-4 mt-4 w-1/2">
                              <div className="">
                                <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                  System Name
                                  <span className="text-red-500">*</span>
                                </label>
                                <input
                                  name="system_name"
                                  value={systemsInfo?.system_name}
                                  // {...registerBusinessUnit("bu_name")}
                                  onChange={handleSystemInputChange}
                                  type="text"
                                  className="w-full
                               p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                  required
                                />
                                {/* <p className="flex-1 p-2 text-gray-500 rounded">
                              {dummySystemData[0]?.system_name}
                            </p> */}
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4 mt-4">
                              <div className="">
                                <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                  Location
                                  <span className="text-red-500">*</span>
                                </label>
                                <input
                                  name="location"
                                  value={systemsInfo?.location}
                                  // {...registerBusinessUnit("bu_name")}
                                  onChange={handleSystemInputChange}
                                  type="text"
                                  required
                                  className="w-full
                               p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                />
                              </div>
                              <div className="">
                                <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                  System Type
                                </label>
                                <select
                                  name="system_type"
                                  value={systemsInfo?.system_type || ""}
                                  onChange={handleSystemInputChange}
                                  className="w-full p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                >
                                  <option value="">Select System Type</option>
                                  <option value="IT">IT</option>
                                  <option value="OT">OT</option>
                                  <option value="Product">Product</option>
                                </select>
                              </div>
                              <div className="">
                                <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                  Owner First Name
                                </label>
                                <input
                                  name="owner_first_name"
                                  value={systemsInfo?.owner_first_name}
                                  // {...registerBusinessUnit("bu_name")}
                                  onChange={handleSystemInputChange}
                                  type="text"
                                  className="w-full
                               p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                />
                              </div>
                              <div className="">
                                <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                  Owner Last Name
                                </label>
                                <input
                                  name="owner_first_name"
                                  value={systemsInfo?.owner_last_name}
                                  // {...registerBusinessUnit("bu_name")}
                                  onChange={handleSystemInputChange}
                                  type="text"
                                  className="w-full
                               p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                />
                              </div>
                              <div className="">
                                <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                  Email<span className="text-red-500">*</span>
                                </label>
                                <input
                                  name="email"
                                  value={systemsInfo?.email}
                                  // {...registerBusinessUnit("bu_name")}
                                  onChange={handleSystemInputChange}
                                  type="text"
                                  className="w-full
                               p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                  required
                                />
                              </div>

                              <div className="">
                                <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                  Shelf Life
                                  <span className="text-red-500">*</span>
                                </label>
                                <input
                                  name="shelf_life"
                                  value={systemsInfo?.shelf_life}
                                  onChange={handleSystemInputChange}
                                  type="number"
                                  className="w-full
                                  p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                  required
                                />
                              </div>
                              <div className="">
                                <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                  Business Criticality
                                </label>
                                <select
                                  name="business_criticality"
                                  value={systemsInfo?.business_criticality}
                                  onChange={handleSystemInputChange}
                                  className="w-full p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                >
                                  <option value="">
                                    Select Business Criticality
                                  </option>
                                  <option value="Very Low">Very Low</option>
                                  <option value="Low">Low</option>
                                  <option value="Moderate">Moderate</option>
                                  <option value="High">High</option>
                                  <option value="Very High">Very High</option>
                                </select>
                              </div>
                              <div className="">
                                <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                  Upgradability
                                </label>

                                <select
                                  name="upgradability"
                                  value={systemsInfo?.upgradability}
                                  onChange={handleSystemInputChange}
                                  className="w-full p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                >
                                  <option value="">Select Upgradability</option>
                                  <option value="Very Easy">Very Easy</option>
                                  <option value="Easy">Easy</option>
                                  <option value="Moderate">Moderate</option>
                                  <option value="Difficult">Difficult</option>
                                  <option value="Very Difficult">
                                    Very Difficult
                                  </option>
                                </select>
                              </div>
                              <div className="">
                                <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                  Crypto Agility
                                </label>

                                <select
                                  name="crypto_agility"
                                  value={systemsInfo?.crypto_agility}
                                  onChange={handleSystemInputChange}
                                  className="w-full p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                >
                                  <option value="">
                                    Select Crypto Agility
                                  </option>
                                  <option value="Very Easy">Very Easy</option>
                                  <option value="Easy">Easy</option>
                                  <option value="Moderate">Moderate</option>
                                  <option value="Difficult">Difficult</option>
                                  <option value="Very Difficult">
                                    Very Difficult
                                  </option>
                                </select>
                              </div>
                              {/* <div className="flex items-center">
                                <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px]">
                                  Data Association
                                </label>
                                <input
                                  name="associated_amdb_table"
                                  value={systemsInfo?.associated_amdb_table}
                                  // {...registerBusinessUnit("bu_name")}
                                  onChange={handleSystemInputChange}
                                  type="text"
                                  className="w-full
                               p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                  required
                                />
                              </div>
                              <div className="flex items-center">
                                <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px]">
                                  CI Association
                                </label>
                                <input
                                  name="associated_cmdb_table"
                                  value={systemsInfo?.associated_cmdb_table}
                                  // {...registerBusinessUnit("bu_name")}
                                  onChange={handleSystemInputChange}
                                  type="text"
                                  className="w-full
                               p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                  required
                                />
                              </div>
                              <div className="flex items-center">
                                <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px]">
                                  System Association
                                </label>
                                <input
                                  name="associated_data_assets_table"
                                  value={
                                    systemsInfo?.associated_data_assets_table
                                  }
                                  // {...registerBusinessUnit("bu_name")}
                                  onChange={handleSystemInputChange}
                                  type="text"
                                  className="w-full
                               p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                  required
                                />
                              </div> */}
                              <div className="">
                                <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                  Time for Migration
                                  <span className="text-red-500">*</span>
                                </label>
                                <input
                                  name="time_for_migration"
                                  value={systemsInfo?.time_for_migration}
                                  onChange={handleSystemInputChange}
                                  type="number"
                                  className="w-full
                                  p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                  required
                                />
                              </div>
                              <div className="">
                                <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                  Risk Tolerance
                                </label>
                                <select
                                  name="risk_tolerance"
                                  value={systemsInfo?.risk_tolerance}
                                  onChange={handleSystemInputChange}
                                  className="w-full p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                >
                                  <option value="">
                                    Select Risk Tolerance
                                  </option>
                                  <option value="Very Low">Very Low</option>
                                  <option value="Low">Low</option>
                                  <option value="Moderate">Moderate</option>
                                  <option value="High">High</option>
                                  <option value="Very High">Very High</option>
                                </select>
                              </div>
                              <div className="">
                                {/* <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px]">
                                  Supplied By
                                </label> */}
                                <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                  Vendor Supplied Asset
                                  <span className="text-red-500">*</span>
                                </label>
                                {/* <input
                                  name="supplied_by"
                                  value={systemsInfo?.supplied_by}
                                  onChange={handleSystemInputChange}
                                  type="text"
                                  className="w-full
                               p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                /> */}
                                <select
                                  name="vendor_supplied_asset"
                                  value={systemsInfo?.vendor_supplied_asset}
                                  onChange={handleSystemInputChange}
                                  className="w-full p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                >
                                  <option value="">Select Option</option>
                                  <option value="Yes">Yes</option>
                                  <option value="No">No</option>
                                </select>
                              </div>
                              <div className="grid grid-cols-1 gap-4 mt-2">
                                <div className="">
                                  <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                    Regulatory Compliance
                                  </label>
                                  <input
                                    name="regulatory_compliance"
                                    value={systemsInfo?.regulatory_compliance}
                                    onChange={handleSystemInputChange}
                                    type="text"
                                    className="w-full
                               p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="flex justify-center mt-4 items-center space-x-4">
                              <button
                                type="button"
                                // onClick={() =>
                                //   handleBusinessEditReset()
                                // }
                                className="px-4 py-2 bg-custom-blue5 text-white rounded hover:bg-custom-blue6"
                              >
                                Reset
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setIsSystemEditable(false);
                                  // setExpandedSection(""); // Close the expanded section
                                  // setSelectedBusinessUnit(""); // Deselect the company
                                  // setShowNewBusinessForm(false);
                                }}
                                className="px-4 py-2 bg-custom-blue5 text-white rounded hover:bg-custom-blue6"
                              >
                                Cancel
                              </button>
                              <button
                                type="submit"
                                className="px-4 py-2 bg-custom-blue6 text-white rounded hover:bg-custom-blue7"
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        </form>
                      ) : (
                        <div>
                          <div className="grid grid-cols-1 gap-4 mt-4 w-1/2">
                            <div className="">
                              <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                System Name
                              </label>
                              <p
                                className={`w-full
                               p-2 border border-custom-blue3 rounded ${
                                 !systemsInfo?.system_name && "h-[41px]"
                               }`}
                              >
                                {systemsInfo?.system_name}
                              </p>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4 mt-4">
                            <div className="">
                              <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                Location
                              </label>
                              <p
                                className={`w-full
                               p-2 border border-custom-blue3 rounded ${
                                 !systemsInfo?.location && "h-[41px]"
                               }`}
                              >
                                {systemsInfo?.location}
                              </p>
                            </div>
                            <div className="">
                              <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                System Type
                              </label>
                              <p
                                className={`w-full
                               p-2 border border-custom-blue3 rounded ${
                                 !systemsInfo?.system_type && "h-[41px]"
                               }`}
                              >
                                {systemsInfo?.system_type}
                              </p>
                            </div>
                            <div className="">
                              <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                Owner First Name
                              </label>
                              <p
                                className={`w-full
                               p-2 border border-custom-blue3 rounded ${
                                 !systemsInfo?.owner_first_name && "h-[41px]"
                               }`}
                              >
                                {systemsInfo?.owner_first_name}
                              </p>
                            </div>
                            <div className="">
                              <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                Owner Last Name
                              </label>
                              <p
                                className={`w-full
                               p-2 border border-custom-blue3 rounded ${
                                 !systemsInfo?.owner_last_name && "h-[41px]"
                               }`}
                              >
                                {systemsInfo?.owner_last_name}
                              </p>
                            </div>
                            <div className="">
                              <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                Email
                              </label>
                              <p
                                className={`w-full
                               p-2 border border-custom-blue3 rounded ${
                                 !systemsInfo?.email && "h-[41px]"
                               }`}
                              >
                                {systemsInfo?.email}
                              </p>
                            </div>

                            <div className="">
                              <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                Shelf Life
                              </label>
                              <p
                                className={`w-full
                                  p-2 border border-custom-blue3 rounded ${
                                    !systemsInfo?.shelf_life && "h-[41px]"
                                  }`}
                              >
                                {systemsInfo?.shelf_life}
                              </p>
                            </div>
                            <div className="">
                              <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                Business Criticality
                              </label>
                              <p
                                className={`w-full
                                  p-2 border border-custom-blue3 rounded ${
                                    !systemsInfo?.business_criticality &&
                                    "h-[41px]"
                                  }`}
                              >
                                {systemsInfo?.business_criticality}
                              </p>
                            </div>
                            <div className="">
                              <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                Upgradability
                              </label>
                              <p
                                className={`w-full
                                  p-2 border border-custom-blue3 rounded ${
                                    !systemsInfo?.upgradability && "h-[41px]"
                                  }`}
                              >
                                {systemsInfo?.upgradability}
                              </p>
                            </div>
                            <div className="">
                              <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                Crypto Agility
                              </label>
                              <p
                                className={`w-full
                                  p-2 border border-custom-blue3 rounded ${
                                    !systemsInfo?.crypto_agility && "h-[41px]"
                                  }`}
                              >
                                {systemsInfo?.crypto_agility}
                              </p>
                            </div>
                            <div className="">
                              <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                Time for Migration
                              </label>
                              <p
                                className={`w-full
                                  p-2 border border-custom-blue3 rounded ${
                                    !systemsInfo?.time_for_migration &&
                                    "h-[41px]"
                                  }`}
                              >
                                {systemsInfo?.time_for_migration}
                              </p>
                            </div>
                            <div className="">
                              <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                Risk Tolerance
                              </label>
                              <p
                                className={`w-full
                                  p-2 border border-custom-blue3 rounded ${
                                    !systemsInfo?.risk_tolerance && "h-[41px]"
                                  }`}
                              >
                                {systemsInfo?.risk_tolerance}
                              </p>
                            </div>
                            <div className="">
                              <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                Vendor Supplied Asset
                              </label>
                              <p
                                className={`w-full
                                  p-2 border border-custom-blue3 rounded ${
                                    !systemsInfo?.vendor_supplied_asset &&
                                    "h-[41px]"
                                  }`}
                              >
                                {systemsInfo?.vendor_supplied_asset}
                              </p>
                            </div>
                            <div className="grid grid-cols-1 gap-4 mt-">
                              <div className="">
                                <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                  Regulatory Compliance
                                </label>
                                <p
                                  className={`w-full
                               p-2 border border-custom-blue3 rounded ${
                                 !systemsInfo?.regulatory_compliance &&
                                 "h-[41px]"
                               }`}
                                >
                                  {systemsInfo?.regulatory_compliance}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    {showNewSystemForm && (
                      <form
                        onSubmit={onSystemFormSubmit(handleSystemDetailsSubmit)}
                      >
                        <div className="grid grid-cols-1 gap-4 mt-4 w-1/2">
                          <div className="">
                            <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                              System Name<span className="text-red-500">*</span>
                            </label>
                            <input
                              {...registerSystem("system_name")}
                              type="text"
                              className="w-full 
                               p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                              required
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 mt-4">
                          <div className="">
                            <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                              Location<span className="text-red-500">*</span>
                            </label>
                            <input
                              {...registerSystem("location")}
                              type="text"
                              className="w-full
                               p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                              required
                            />
                          </div>
                          <div className="">
                            <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                              System Type
                            </label>
                            <select
                              {...registerSystem("system_type")}
                              defaultValue="IT"
                              className="w-full p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                            >
                              <option value="">Select System Type</option>
                              <option value="IT">IT</option>
                              <option value="OT">OT</option>
                              <option value="Product">Product</option>
                            </select>
                          </div>
                          <div className="">
                            <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                              Owner First Name
                            </label>
                            <input
                              {...registerSystem("owner_first_name")}
                              type="text"
                              className="w-full
                               p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                            />
                          </div>
                          <div className="">
                            <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                              Owner Last Name
                            </label>
                            <input
                              {...registerSystem("owner_last_name")}
                              type="text"
                              className="w-full
                               p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                            />
                          </div>
                          <div className="">
                            <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                              Email<span className="text-red-500">*</span>
                            </label>
                            <input
                              {...registerSystem("email")}
                              type="text"
                              className="w-full
                               p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                              required
                            />
                          </div>

                          <div className="">
                            <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                              Shelf Life<span className="text-red-500">*</span>
                            </label>
                            <input
                              {...registerSystem("shelf_life", {
                                valueAsNumber: true,
                              })}
                              type="number"
                              className="w-full
                               p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                              required
                            />
                          </div>
                          <div className="">
                            <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                              Business Criticality
                            </label>
                            <select
                              {...registerSystem("business_criticality")}
                              defaultValue="Low"
                              className="w-full p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                            >
                              <option value="">
                                Select Business Criticality
                              </option>
                              <option value="Very Low">Very Low</option>
                              <option value="Low">Low</option>
                              <option value="Moderate">Moderate</option>
                              <option value="High">High</option>
                              <option value="Very High">Very High</option>
                            </select>
                          </div>
                          <div className="">
                            <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                              Upgradability
                            </label>
                            <select
                              {...registerSystem("upgradability")}
                              defaultValue="Easy"
                              className="w-full p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                            >
                              <option value="">Select Upgradability</option>
                              <option value="Very Easy">Very Easy</option>
                              <option value="Easy">Easy</option>
                              <option value="Moderate">Moderate</option>
                              <option value="Difficult">Difficult</option>
                              <option value="Very Difficult">
                                Very Difficult
                              </option>
                            </select>
                          </div>
                          <div className="">
                            <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                              Crypto Agility
                            </label>
                            <select
                              {...registerSystem("crypto_agility")}
                              defaultValue="L2: Defined"
                              className="w-full p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                            >
                              <option value="">Select Crypto Agility</option>
                              <option value="L0: Initial">L0: Initial</option>
                              <option value="L1: Developing">
                                L1: Developing
                              </option>
                              <option value="L2: Defined">L2: Defined</option>
                              <option value="L3: Managed">L3: Managed</option>
                              <option value="L4: Optimized">
                                L4: Optimized
                              </option>
                            </select>
                          </div>
                          {/* <div className="">
                            <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                              Data Association
                            </label>
                            <input
                              {...registerSystem("associated_amdb_table")}
                              type="text"
                              className="w-full
                               p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                            />
                          </div>
                          <div className="">
                            <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                              CI Association
                            </label>
                            <input
                              {...registerSystem("associated_cmdb_table")}
                              type="text"
                              className="w-full
                               p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                            />
                          </div>
                          <div className="">
                            <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                              Data Assets Association
                            </label>
                            <input
                              {...registerSystem(
                                "associated_data_assets_table"
                              )}
                              type="text"
                              className="w-full
                               p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                            />
                          </div> */}
                          <div className="">
                            <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                              Time for Migration
                              <span className="text-red-500">*</span>
                            </label>
                            <input
                              {...registerSystem("time_for_migration", {
                                valueAsNumber: true,
                              })}
                              type="number"
                              className="w-full
                               p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                              required
                            />
                          </div>
                          <div className="">
                            <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                              Risk Tolerance
                            </label>
                            <select
                              {...registerSystem("risk_tolerance")}
                              defaultValue="Low"
                              className="w-full p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                            >
                              <option value="">Select Risk Tolerance</option>
                              <option value="Very Low">Very Low</option>
                              <option value="Low">Low</option>
                              <option value="Moderate">Moderate</option>
                              <option value="High">High</option>
                              <option value="Very High">Very High</option>
                            </select>
                          </div>
                          <div className="">
                            <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                              Vendor Supplied Asset
                              <span className="text-red-500">*</span>
                            </label>
                            <select
                              {...registerSystem("vendor_supplied_asset")}
                              className="w-full p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                            >
                              <option value="">Select Option</option>
                              <option value="Yes">Yes</option>
                              <option value="No">No</option>
                            </select>
                          </div>
                          <div className="grid grid-cols-1 gap-4 mt-2">
                            <div className="">
                              <label className="text-custom-blue6 text-sm font-medium mr-2 min-w-[150px] block mb-2">
                                Regulatory Compliance
                              </label>
                              <input
                                {...registerSystem("regulatory_compliance")}
                                type="text"
                                className="w-full
                               p-2 border border-custom-blue3 outline-none focus:border-custom-blue6 rounded"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-center mt-4 items-center space-x-4">
                          <button
                            type="button"
                            onClick={() => resetSystem()}
                            className="px-4 py-2 bg-custom-blue5 text-white rounded hover:bg-custom-blue6"
                          >
                            Reset
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              // setIsSystemEditable(false);
                              resetSystem();
                              setExpandedSection("");
                              setSelectedSystem("");
                              setShowNewSystemForm(false);
                            }}
                            className="px-4 py-2 bg-custom-blue5 text-white rounded hover:bg-custom-blue6"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="px-4 py-2 bg-custom-blue6 text-white rounded hover:bg-custom-blue7"
                          >
                            Add System
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <div className="mb-4 px-4 py-3 sm:px-6 flex justify-center space-x-12 items-center">
          {/* <button
            type="button"
            className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-custom-blue5 text-base font-medium text-white hover:bg-custom-blue7 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-custom-blue7 sm:ml-3 sm:w-auto sm:text-sm cursor-pointer`}
          >
            Reset
          </button> */}
          <button
            type="button"
            className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-custom-blue5 text-base font-medium text-white hover:bg-custom-blue7 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-custom-blue7 sm:ml-3 sm:w-auto sm:text-sm cursor-pointer`}
            onClick={() => {
              navigate("/dashboard");
            }}
            // disabled={isEditing}
          >
            Cancel
          </button>
          {/* <button
        type="button"
        className={`w-full inline-flex justify-center rounded-md shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm 
          ${isButtonEnabled ? "bg-custom-blue7 hover:bg-custom-blue6" : "bg-custom-blue4 cursor-not-allowed"}`}
        onClick={() => {
          if (isButtonEnabled) {
            handleCreateProject();
          }
        }}
        disabled={!isButtonEnabled}
      >
        Create Project
      </button> */}
          {/* Render "Create Project" button if no project is selected */}
          {state.newProject === "new" ? (
            <button
              type="button"
              className={`w-full inline-flex justify-center items-center rounded-md shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm 
                ${
                  canICreateProject
                    ? "bg-custom-blue7 hover:bg-custom-blue6"
                    : "bg-custom-blue4 cursor-not-allowed"
                }`}
              onClick={() => {
                if (canICreateProject) {
                  handleCreateProject();
                }
              }}
              disabled={!canICreateProject || loading.creatingProject}
            >
              {loading.creatingProject ? (
                <>
                  <TailSpin height={16} width={15} color="white" />
                  <span className="ml-2">Please wait...</span>
                </>
              ) : (
                "Create Project"
              )}
            </button>
          ) : (
            /* Render "Save" button if a project is selected */
            <button
              type="button"
              className={
                "w-full inline-flex justify-center rounded-md shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm bg-custom-blue7 hover:bg-custom-blue6"
              }
              onClick={handleSaveProject}
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectCreationForm;
